<!-- White check for dark/selected surfaces (the shared ui-kit check is dark grey → invisible on teal). -->
<ng-template #whiteCheck>
  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="flex-shrink-0">
    <path
      d="M7.5 12.83A5.83 5.83 0 1 1 7.5 1.16a5.83 5.83 0 0 1 0 11.67ZM6.92 9.33l4.12-4.12-.82-.83-3.3 3.3-1.65-1.65-.83.82L6.92 9.33Z"
      fill="#ffffff"
    />
  </svg>
</ng-template>

<ng-template #planOptionCard let-product>
  @let isEnterprise = product.metadata['checkout'] === 'false';
  @let isPayg = isPayAsYouGo(product);

  <div
    (click)="choosePlan(product)"
    (keydown.enter)="choosePlan(product)"
    (keydown.space)="choosePlan(product)"
    tabindex="0"
    role="radio"
    data-testid="plan-card"
    [attr.aria-checked]="selectedPlan()?.id === product.id"
    class="text-left rounded-xl border p-5 flex flex-col h-full transition-colors cursor-pointer"
    [class.border-[#5B9BAD]]="selectedPlan()?.id === product.id"
    [class.bg-[#5B9BAD0D]]="selectedPlan()?.id === product.id"
    [class.border-[#E5E7EB]]="selectedPlan()?.id !== product.id"
  >
    @if (planLogo(product); as logo) {
      <img
        [src]="logo"
        [alt]="'membership.plan.' + (product.name | lowercase) | transloco"
        width="86"
        height="83"
        class="w-[64px] h-[62px] object-cover mb-3 rounded-lg"
      />
    }

    <p class="font-semibold text-lg">{{ 'membership.plan.' + (product.name | lowercase) | transloco }}</p>
    @if (isPayg) {
      <!--
        Pay as you go has no plan fee: the price shown is one report. Checkout charges the first
        report (the product's one-time price); every later one is a top-up.
      -->
      <p class="font-semibold text-2xl my-2" data-testid="payg-headline">
        <span class="text-sm font-normal text-[#6A7282]">{{ 'membership.plans.flow.payg.from' | transloco }}</span>
        {{
          product.defaultPrice.unitAmount / 100
            | currency: (product.defaultPrice.currency | uppercase) : 'symbol' : '1.0-0'
        }}
        <span class="text-sm font-normal text-[#6A7282]">{{
          'membership.plans.flow.payg.per-report' | transloco
        }}</span>
      </p>
      <p class="text-xs text-[#6A7282]">{{ 'membership.plans.flow.payg.no-fee' | transloco }}</p>
    } @else if (!isEnterprise) {
      <p class="font-semibold text-2xl my-2">
        {{
          product.defaultPrice.unitAmount / 100
            | currency: (product.defaultPrice.currency | uppercase) : 'symbol' : '1.0-0'
        }}
        <span class="text-sm font-normal text-[#6A7282]">{{ 'membership.plans.flow.per-year' | transloco }}</span>
      </p>
      <p class="text-xs text-[#6A7282]">
        {{ product.metadata['number_of_searches'] }} {{ 'common.number-of-searches' | transloco }}
      </p>
    } @else {
      <p class="font-semibold text-2xl my-2">{{ 'common.custom' | transloco }}</p>
    }

    @if (descriptionItems(product).length) {
      <hr class="my-3 w-full" />
      <p class="uppercase text-[#6A7282] font-bold text-[11px] tracking-wide">
        {{ 'membership.plans.description.title' | transloco }}
      </p>
      <ul class="mt-2 flex flex-col gap-y-2">
        @for (item of descriptionItems(product); track $index) {
          <li class="flex gap-x-2">
            <ui-kit-checkbox-circle-fill />
            <p class="text-xs">{{ item }}</p>
          </li>
        }
      </ul>
    }

    @if (selectedPlan()?.id === product.id) {
      <ui-kit-badge type="primary" [additionalClasses]="'rounded-lg font-normal'" size="xs" class="mt-4 self-start">
        {{ 'membership.plans.flow.addons.selected' | transloco }}
      </ui-kit-badge>
    }
  </div>
</ng-template>

<!--
  Add-on picker grouped by PRODUCT (one section per add-on). Cyber exposure report is a single
  add-on with billing cadences → one card with an internal radio picker (incl. "No thanks");
  the AI add-ons are distinct products → one checkbox card each.
-->
<ng-template #cyberRadioRow let-product="product" let-label="label" let-price="price" let-priceType="priceType" let-selected="selected">
  <div
    (click)="selectCyberCadence(product)"
    (keydown.enter)="selectCyberCadence(product)"
    (keydown.space)="selectCyberCadence(product)"
    tabindex="0"
    role="radio"
    [attr.aria-checked]="selected"
    class="rounded-md p-3 border cursor-pointer flex items-center justify-between gap-2 transition-colors"
    [class.border-[#5B9BAD]]="selected"
    [class.bg-[#5B9BAD0D]]="selected"
    [class.border-[#E5E7EB]]="!selected"
  >
    <div class="flex items-center gap-2 text-sm font-medium text-[#364153]">
      <div
        class="flex items-center justify-center w-4 h-4 rounded-full border-2 flex-shrink-0"
        [class.border-[#5B9BAD]]="selected"
        [class.border-[#D1D5DB]]="!selected"
      >
        @if (selected) {
          <div class="w-2 h-2 rounded-full bg-[#5B9BAD]"></div>
        }
      </div>
      {{ label }}
    </div>
    @if (price !== null) {
      <p class="font-bold text-sm whitespace-nowrap">
        {{ price / 100 | currency: (currency() | uppercase) : 'symbol' : '1.0-0' }}
        <span class="text-xs font-normal text-[#6A7282]">
          {{ (priceType === 'one_time' ? 'membership.plans.flow.one-time' : 'membership.plans.flow.per-year') | transloco }}
        </span>
      </p>
    }
  </div>
</ng-template>

<ng-template #cyberCadencePicker>
  <ng-container
    [ngTemplateOutlet]="cyberRadioRow"
    [ngTemplateOutletContext]="{
      product: null,
      label: ('membership.plans.flow.addons.no-thanks' | transloco),
      price: null,
      priceType: null,
      selected: !selectedCyberProduct(),
    }"
  />
  @for (cadence of cyberCadences(); track cadence.id) {
    <ng-container
      [ngTemplateOutlet]="cyberRadioRow"
      [ngTemplateOutletContext]="{
        product: cadence.product,
        label: cadence.label,
        price: cadence.priceMinor,
        priceType: cadence.priceType,
        selected: cadence.selected,
      }"
    />
  }
</ng-template>

<!-- Variant 4 "fancy" add-on presentation: coloured header bands + white check on dark. -->
<ng-template #addonSectionsFancy>
  <div class="flex flex-col gap-6">
    @if (showCyberPicker()) {
      <section class="rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid="cyber-section">
        <div class="bg-[#0D6373] px-4 py-3">
          <p class="uppercase text-white font-bold text-[11px] tracking-wide">
            {{ 'membership.plans.flow.addons.cyber.title' | transloco }}
          </p>
          <p class="text-xs text-white/80 mt-0.5">{{ 'membership.plans.flow.addons.cyber.description' | transloco }}</p>
        </div>
        <div class="p-4 flex flex-col gap-2">
          <ng-container [ngTemplateOutlet]="cyberCadencePicker" />
        </div>
      </section>
    } @else {
      <ng-container [ngTemplateOutlet]="includedCyberBadge" />
    }

    @if (aiCards().length) {
      <section>
        <p class="uppercase text-[#0D6373] font-bold text-[11px] tracking-wide mb-3">
          {{ 'membership.plans.flow.addons.ai-group' | transloco }}
        </p>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          @for (card of aiCards(); track card.id) {
            <div
              (click)="toggleAiAddon(card.product)"
              (keydown.enter)="toggleAiAddon(card.product)"
              (keydown.space)="toggleAiAddon(card.product)"
              tabindex="0"
              role="checkbox"
              data-testid="ai-card"
              [attr.aria-checked]="card.selected"
              class="rounded-xl border overflow-hidden cursor-pointer transition-all flex flex-col"
              [class.border-[#5B9BAD]]="card.selected"
              [class.shadow-md]="card.selected"
              [class.border-[#E5E7EB]]="!card.selected"
            >
              <div
                class="px-4 py-3 flex items-center justify-between gap-2"
                [class]="card.selected ? 'bg-[#0D6373]' : 'bg-[#F7FAFB]'"
              >
                @if (card.badge) {
                  <ui-kit-badge [type]="card.selected ? 'action' : 'default'" size="xs">{{ card.badge }}</ui-kit-badge>
                } @else {
                  <span></span>
                }
                @if (card.selected) {
                  <ng-container [ngTemplateOutlet]="whiteCheck" />
                } @else {
                  <div class="w-4 h-4 rounded border-2 border-[#D1D5DB] flex-shrink-0 bg-white"></div>
                }
              </div>
              <div class="p-4 flex flex-col gap-1 flex-1">
                <p class="font-semibold text-sm">{{ card.title }}</p>
                @for (line of card.description; track $index) {
                  <p class="text-xs text-[#4A5565]">{{ line }}</p>
                }
                <p class="font-bold mt-auto pt-2">
                  {{ card.priceMinor / 100 | currency: (currency() | uppercase) : 'symbol' : '1.0-0' }}
                  <span class="text-xs font-normal text-[#6A7282]">
                    {{ (card.priceType === 'one_time' ? 'membership.plans.flow.one-time' : 'membership.plans.flow.per-year') | transloco }}
                  </span>
                </p>
              </div>
            </div>
          }
        </div>
      </section>
    }
  </div>
</ng-template>

<ng-template #includedCyberBadge>
  @if (includedCyberCount(); as count) {
    <div class="bg-[#F7FAFB] border border-[#0D6373] p-3 rounded-lg">
      <div class="inline-block"><ui-kit-badge type="primary" size="xs">x{{ count }} {{ 'membership.plans.flow.addons.included' | transloco }}</ui-kit-badge></div>
      <p class="font-semibold text-sm mt-2">{{ 'membership.plans.flow.addons.cyber.title' | transloco }}</p>
    </div>
  }
</ng-template>

<ng-template #orderSummary>
  <div class="rounded-xl border border-[#E5E7EB] p-5 flex flex-col gap-4">
    <p class="font-semibold text-sm uppercase text-[#6A7282]">{{ 'membership.plans.flow.confirm.title' | transloco }}</p>

    @if (selectedPlan(); as plan) {
      <div class="flex justify-between items-start gap-2" data-testid="summary-plan-row">
        <div>
          <p class="text-xs text-[#6A7282]">{{ 'membership.plans.flow.confirm.plan' | transloco }}</p>
          <p class="font-semibold text-sm">{{ 'membership.plan.' + (plan.name | lowercase) | transloco }}</p>
          @if (isPayAsYouGo(plan)) {
            <p class="text-xs text-[#6A7282]">{{ 'membership.plans.flow.payg.first-report' | transloco }}</p>
          }
        </div>
        <p class="font-semibold text-sm whitespace-nowrap">
          {{ plan.defaultPrice.unitAmount / 100 | currency: (currency() | uppercase) : 'symbol' : '1.0-0' }}
        </p>
      </div>

      <hr />
    }

    <div>
      <p class="text-xs text-[#6A7282] mb-2">{{ 'membership.plans.flow.confirm.addons' | transloco }}</p>
      @if (selectedAddons().length) {
        <div class="flex flex-col gap-2">
          @for (addon of selectedAddons(); track addon.id) {
            <div class="flex justify-between items-center gap-2">
              <p class="text-sm">{{ addonDisplayName(addon) }}</p>
              <p class="text-sm font-medium whitespace-nowrap">
                {{
                  addon.defaultPrice.unitAmount / 100
                    | currency: (addon.defaultPrice.currency | uppercase) : 'symbol' : '1.0-0'
                }}
              </p>
            </div>
          }
        </div>
      } @else {
        <p class="text-sm text-[#6A7282]">{{ 'membership.plans.flow.confirm.none' | transloco }}</p>
      }
    </div>

    <hr />

    <div class="flex justify-between items-center">
      <p class="font-semibold">{{ 'membership.plans.flow.confirm.total' | transloco }}</p>
      <p class="font-semibold text-lg">
        {{ totalPriceMinor() / 100 | currency: (currency() | uppercase) : 'symbol' : '1.0-0' }}
      </p>
    </div>
  </div>
</ng-template>

<!--
  Repeat: two free tabs, styled like ui-kit-tabs. That component's active pill is white on the
  page background, so the dialog's white surface needs the page tone behind the list.
-->
<ng-template #repeatTabs>
  <div class="flex justify-center flex-shrink-0">
    <ul class="flex items-center flex-wrap rounded-full bg-black-haze-50 p-1" role="tablist">
      @for (label of visibleSteps(); track label; let i = $index) {
        <li
          class="text-sm text-typography-muted rounded-full"
          role="tab"
          [attr.aria-selected]="isStepActive(i)"
          [class.text-typography-primary]="isStepActive(i)"
          [class.bg-white]="isStepActive(i)"
        >
          <button
            type="button"
            data-testid="step-entry"
            class="min-w-24 text-center px-[11px] py-[6px]"
            [class.cursor-default]="!isStepReachable(i)"
            (click)="goToStep(i)"
          >
            {{ label | transloco }}
          </button>
        </li>
      }
    </ul>
  </div>
</ng-template>

<!-- First purchase: clickable header of three gated steps. -->
<ng-template #stepHeader>
  <div class="flex items-center gap-3 flex-shrink-0">
    @for (label of visibleSteps(); track label; let i = $index) {
      <button
        type="button"
        data-testid="step-entry"
        class="flex-1 text-left"
        [class.cursor-pointer]="isStepReachable(i)"
        [class.cursor-default]="!isStepReachable(i)"
        (click)="goToStep(i)"
      >
        <p
          class="text-xs font-semibold mb-1"
          [class.text-[#0D6373]]="isStepActive(i)"
          [class.text-[#9CA3AF]]="!isStepActive(i)"
        >
          {{ i + 1 }}. {{ label | transloco }}
        </p>
        <ui-kit-progress-bar type="primary" [percentage]="isStepActive(i) ? 100 : 0" />
      </button>
    }
  </div>
</ng-template>

<!-- Variant 4: deferred-footer wizard — footer appears only once a plan is picked -->
<div class="flex flex-col max-h-[68vh]">
  @if (showStepHeader()) {
    <div class="mb-6">
      <ng-container [ngTemplateOutlet]="isRepeat() ? repeatTabs : stepHeader" />
    </div>
  }

  <div class="flex-1 min-h-0 overflow-y-auto pr-1">
    @switch (step()) {
      @case (0) {
        <div
          class="step-transition grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="plan-grid"
          [class.lg:grid-cols-4]="visiblePlans().length <= 4"
          [class.lg:grid-cols-5]="visiblePlans().length > 4"
        >
          @for (product of visiblePlans(); track product.id) {
            <ng-container [ngTemplateOutlet]="planOptionCard" [ngTemplateOutletContext]="{ $implicit: product }" />
          }
        </div>
      }
      @case (1) {
        <div class="step-transition flex flex-col gap-5">
          <div>
            <h2 class="font-semibold text-lg">{{ 'membership.plans.flow.addons.title' | transloco }}</h2>
            <p class="text-sm text-[#6A7282]">{{ 'membership.plans.flow.addons.subtitle' | transloco }}</p>
          </div>
          <ng-container [ngTemplateOutlet]="addonSectionsFancy" />
        </div>
      }
      @case (2) {
        <div class="step-transition max-w-md mx-auto w-full">
          <ng-container [ngTemplateOutlet]="orderSummary" />
        </div>
      }
    }
  </div>

  @if (showFooter()) {
    <div
      class="step-transition flex items-center justify-between gap-4 pt-4 mt-4 border-t border-[#E5E7EB] flex-shrink-0"
      data-testid="flow-footer"
    >
      @if (isEnterpriseSelected()) {
        <p class="font-semibold">{{ 'membership.plan.enterprise' | transloco }}</p>
        <a ui-kit-button [href]="'mailto:' + enterprisePlanEmail" class="!inline-flex">
          {{ 'membership.plans.no-checkout.cta' | transloco }}
        </a>
      } @else {
        <p class="font-semibold">
          {{ 'membership.plans.flow.confirm.total' | transloco }}:
          {{ totalPriceMinor() / 100 | currency: (currency() | uppercase) : 'symbol' : '1.0-0' }}
        </p>
        <div class="flex gap-3">
          @switch (step()) {
            @case (0) {
              <!--
                No [disabled] here: this button only navigates to the add-on tab (a repeat
                buyer may have no plan yet and nothing else selected). The empty-cart guard
                belongs on the steps that move towards money — confirm() and the buttons on
                @case (1) and @case (2).
              -->
              <button ui-kit-button (click)="continueFromPlan()">
                {{ 'membership.plans.flow.addons.continue' | transloco }}
              </button>
            }
            @case (1) {
              @if (selectedPlan()) {
                <button ui-kit-button variant="stroked" (click)="skipAddons()">
                  {{ 'membership.plans.flow.addons.skip' | transloco }}
                </button>
              }
              <button ui-kit-button [disabled]="isCartEmpty()" (click)="proceedToConfirm()">
                {{ 'membership.plans.flow.addons.continue' | transloco }}
              </button>
            }
            @case (2) {
              <button ui-kit-button variant="stroked" (click)="backToPlan()">
                {{ 'membership.plans.flow.change-plan' | transloco }}
              </button>
              <button ui-kit-button [disabled]="isCartEmpty()" (click)="confirm()">
                {{ 'membership.plans.flow.confirm.cta' | transloco }}
              </button>
            }
          }
        </div>
      }
    </div>
  }
</div>
